import {
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_PARAGRAPH,
    BLOCK_STEPPER,
    BLOCK_TABLE,
    buttons,
    code,
    columns,
    hint,
    INLINE_ICON,
    INLINE_LINK,
    images,
    MARK_BOLD,
    MARK_CODE,
    paragraph,
    tabs,
} from '@gitbook/doc-core';
import { createStepperStep } from '@gitbook/doc-core/src/stepper';
import { Block, Fragment, generateKey, Inline, Mark, Text } from '@gitbook/slate';

import { getRandomLexoRank } from '../../../../../../util-orderindex/src';

/**
 * Basic blocks for the quickstart.
 * It includes a heading, a paragraph, and a table with cards.
 */
export const introBlocks = [
    Block.create({ type: BLOCK_HEADING_1, nodes: [Text.create('Welcome to Gitbook')] }),
    paragraph.createParagraph(
        "Here you'll get an overview of all the amazing features GitBook offers to help you build beautiful, interactive documentation."
    ),
    paragraph.createParagraph(
        "You'll see some of the best parts of GitBook in action — and find help on how you can turn this template into your own."
    ),
    Block.create({ type: BLOCK_HEADING_2, nodes: [Text.create('Jump right in')] }),
    createCardsWithCovers(),
];

/**
 * Two columns blocks for the quickstart.
 * It includes a heading, a paragraph, and two columns.
 */
export const twoColumnsBlocks = [
    Block.create({ type: BLOCK_HEADING_1, nodes: [Text.create('Quickstart')] }),
    paragraph.createParagraph(
        'Beautiful documentation starts with the content you create — and GitBook makes it easy to get started with any pre-existing content'
    ),
    columns.createColumnsBlock([
        [
            images.createImageBlock({
                kind: 'url',
                url: 'https://gitbookio.github.io/space-quickstart-images/import-panel.svg',
            }),
        ],
        [
            Block.create({
                type: BLOCK_HEADING_3,
                nodes: [Text.create('Import')],
            }),
            paragraph.createParagraph(
                'GitBook supports importing content from many popular writing tools and formats. If your content already exists, you can upload a file or group of files to be imported.'
            ),
            Block.create({
                type: BLOCK_PARAGRAPH,
                nodes: [
                    buttons.createInlineButton({
                        label: 'Learn more',
                        ref: {
                            kind: 'url',
                            url: 'https://gitbook.com/docs/getting-started/import',
                        },
                    }),
                ],
            }),
        ],
    ]),
    columns.createColumnsBlock([
        [
            Block.create({
                type: BLOCK_HEADING_3,
                nodes: [Text.create('Sync a repository')],
            }),
            paragraph.createParagraph(
                'GitBook also allows you to set up a bi-directional sync with an existing repository on GitHub or GitLab. Setting up Git Sync allows you and your team to write content in GitBook or in code, and never have to worry about your content becoming out of sync.'
            ),
            Block.create({
                type: BLOCK_PARAGRAPH,
                nodes: [
                    buttons.createInlineButton({
                        label: 'Connect now',
                        ref: {
                            kind: 'url',
                            url: 'https://gitbook.com/docs/getting-started/git-sync',
                        },
                    }),
                ],
            }),
        ],
        [
            images.createImageBlock({
                kind: 'url',
                url: 'https://gitbookio.github.io/space-quickstart-images/sync-repo.svg',
            }),
        ],
    ]),
];

/**
 * Multi-syntax code blocks for the quickstart.
 * It includes a heading, a paragraph, and a tabs block with code blocks.
 */
export const multiSyntaxCodeBlocks = [
    Block.create({ type: BLOCK_HEADING_1, nodes: [Text.create('Multi-syntax code')] }),
    paragraph.createParagraph('You can add code to your GitBook pages using code blocks.'),
    paragraph.createParagraph([
        Text.create('When you add a code block, you can choose to '),
        Inline.create({
            type: INLINE_LINK,
            nodes: [Text.create('set the syntax')],
            data: {
                ref: {
                    kind: 'url',
                    url: 'https://gitbook.com/docs/creating-content/blocks/code-block#set-syntax',
                },
            },
        }),
        Text.create(', '),
        Inline.create({
            type: INLINE_LINK,
            nodes: [Text.create('show line numbers')],
            data: {
                ref: {
                    kind: 'url',
                    url: 'https://gitbook.com/docs/creating-content/blocks/code-block#with-line-numbers',
                },
            },
        }),
        Text.create(', '),
        Inline.create({
            type: INLINE_LINK,
            nodes: [Text.create('show a caption')],
            data: {
                ref: {
                    kind: 'url',
                    url: 'https://gitbook.com/docs/creating-content/blocks/code-block#with-caption',
                },
            },
        }),
        Text.create(', and '),
        Inline.create({
            type: INLINE_LINK,
            nodes: [Text.create('wrap the lines')],
            data: {
                ref: {
                    kind: 'url',
                    url: 'https://gitbook.com/docs/creating-content/blocks/code-block#wrap-code',
                },
            },
        }),
        Text.create('. It’s also easy to '),
        Inline.create({
            type: INLINE_LINK,
            nodes: [Text.create('copy the contents of a code block to the clipboard')],
            data: {
                ref: {
                    kind: 'url',
                    url: 'https://gitbook.com/docs/creating-content/blocks/code-block#copying-the-code',
                },
            },
        }),
        Text.create(', so you can use it elsewhere.'),
    ]),
    Block.create({ type: BLOCK_HEADING_2, nodes: [Text.create('Example of code block')] }),
    tabs.createTabs([
        tabs.createTabsItem('JavaScript', [
            code.createCodeBlock('const message = "hello world";\nconsole.log(message);', {
                syntax: 'javascript',
            }),
        ]),
        tabs.createTabsItem('Python', [
            code.createCodeBlock('message = "hello world"\nprint(message)', {
                syntax: 'python',
            }),
        ]),
        tabs.createTabsItem('Ruby', [
            code.createCodeBlock('message = "hello world"\nputs message', {
                syntax: 'ruby',
            }),
        ]),
    ]),
    hint.createHint({
        nodes: [
            paragraph.createParagraph([
                Text.create('You can make code blocks '),
                Inline.create({
                    type: INLINE_LINK,
                    nodes: [Text.create('span the full width of your window')],
                    data: {
                        ref: {
                            kind: 'url',
                            url: 'https://gitbook.com/docs/creating-content/blocks#full-width-blocks',
                        },
                    },
                }),
                Text.create(' by clicking on the '),
                Text.create({
                    text: 'Options menu ',
                    marks: [Mark.create({ type: MARK_BOLD })],
                }),
                Inline.create({
                    type: INLINE_ICON,
                    data: {
                        icon: 'grip-dots-vertical',
                    },
                }),
                Text.create(' icon in GitBook next to the block and choosing '),
                Text.create({
                    text: 'Full width.',
                    marks: [Mark.create({ type: MARK_BOLD })],
                }),
            ]),
        ],
        style: 'info',
    }),
];

/**
 * Stepper blocks for the quickstart.
 * It includes a heading, a paragraph, and a stepper block.
 */
export const stepperBlocks = [
    Block.create({ type: BLOCK_HEADING_1, nodes: [Text.create('Step-by-step guide')] }),
    paragraph.createParagraph(
        'Stepper blocks let you break down a tutorial or guide into separate, but clearly linked steps. Each step can contain multiple different blocks, allowing you to add detailed information.'
    ),
    Block.create({
        type: BLOCK_STEPPER,
        nodes: [
            createStepperStep(
                [Text.create('Add a stepper block')],
                [
                    paragraph.createParagraph([
                        Text.create('To add a stepper block, hit '),
                        Text.create({
                            text: '/',
                            marks: [Mark.create({ type: MARK_CODE })],
                        }),
                        Text.create(' on an empty line or click the '),
                        Text.create({
                            text: '+',
                            marks: [Mark.create({ type: MARK_CODE })],
                        }),
                        Text.create(' on the left of the editor and select '),
                        Text.create({
                            text: 'Stepper',
                            marks: [Mark.create({ type: MARK_BOLD })],
                        }),
                        Text.create(' from the insert menu.'),
                    ]),
                ]
            ),
            createStepperStep(
                [Text.create('Add some content')],
                [
                    paragraph.createParagraph(
                        'Once you’ve inserted your stepper block, you can start adding content to it — including code blocks, drawings, images and much more.'
                    ),
                ]
            ),
            createStepperStep(
                [Text.create('Add more steps')],
                [
                    paragraph.createParagraph([
                        Text.create('Click the '),
                        Text.create({
                            text: '+',
                            marks: [Mark.create({ type: MARK_CODE })],
                        }),
                        Text.create(' below the step numbers or hit '),
                        Text.create({
                            text: 'Enter',
                            marks: [Mark.create({ type: MARK_CODE })],
                        }),
                        Text.create(
                            ' twice to add another step to your stepper block. You can remove or change the style of the step header or step body if you wish.'
                        ),
                    ]),
                ]
            ),
        ],
    }),
];

/**
 * Create 3 cards with cover title and description.
 */
function createCardsWithCovers() {
    const imageColumnId = generateKey();
    const titleColumnId = generateKey();
    const descColumnId = generateKey();

    const rank = getRandomLexoRank();
    const rank2 = rank.genNext();
    const rank3 = rank2.genNext();

    const recordId1 = generateKey();
    const recordId2 = generateKey();
    const recordId3 = generateKey();

    // Create fragments for the title (as heading) and description
    const titleFragment1 = Fragment.create({
        nodes: [Block.create({ type: BLOCK_HEADING_3, nodes: [Text.create('Quickstart')] })],
    });

    const titleFragment2 = Fragment.create({
        nodes: [Block.create({ type: BLOCK_HEADING_3, nodes: [Text.create('Editor basics')] })],
    });

    const titleFragment3 = Fragment.create({
        nodes: [Block.create({ type: BLOCK_HEADING_3, nodes: [Text.create('Publish your docs')] })],
    });

    return Block.create({
        isVoid: true,
        type: BLOCK_TABLE,
        data: {
            records: {
                [recordId1]: {
                    values: {
                        [imageColumnId]: {
                            ref: {
                                kind: 'url',
                                url: 'https://gitbookio.github.io/space-quickstart-images/public-docs-card.svg',
                            },
                        },
                        [titleColumnId]: titleFragment1.fragmentKey,
                        [descColumnId]: 'Create your first site',
                    },
                    orderIndex: rank.toString(),
                },
                [recordId2]: {
                    values: {
                        [imageColumnId]: {
                            ref: {
                                kind: 'url',
                                url: 'https://gitbookio.github.io/space-quickstart-images/basics-card.svg',
                            },
                        },
                        [titleColumnId]: titleFragment2.fragmentKey,
                        [descColumnId]: 'Learn the basics of GitBook',
                    },
                    orderIndex: rank2.toString(),
                },
                [recordId3]: {
                    values: {
                        [imageColumnId]: {
                            ref: {
                                kind: 'url',
                                url: 'https://gitbookio.github.io/space-quickstart-images/git-sync-card.svg',
                            },
                        },
                        [titleColumnId]: titleFragment3.fragmentKey,
                        [descColumnId]: 'Share your docs online',
                    },
                    orderIndex: rank3.toString(),
                },
            },
            definition: {
                [imageColumnId]: {
                    id: imageColumnId,
                    type: 'image',
                },
                [titleColumnId]: {
                    id: titleColumnId,
                    type: 'text',
                    textAlignment: 'left',
                },
                [descColumnId]: {
                    id: descColumnId,
                    type: 'text',
                    textAlignment: 'left',
                },
            },
            view: {
                type: 'cards',
                columns: [titleColumnId, descColumnId],
                coverDefinition: imageColumnId, // This makes the image column the cover!
            },
        },
        fragments: [titleFragment1, titleFragment2, titleFragment3],
    });
}
